import React from 'react';
import { Typography } from 'pvs-design-system';
import { Box } from '@mui/material';
import { getFileType, getFileExtension } from '../../utils/fileUtils';
import type { ItemType } from 'types';

interface TypeIconProps {
  type: ItemType;
  size?: number;
}

// Icon configuration with colors and SVGs for different file types
const TYPE_ICON_CONFIG: Record<string, { bg: string; color: string; svg: React.ReactNode }> = {
  guide: {
    bg: '#E3F2FD',
    color: '#0D47A1',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>
      </svg>
    ),
  },
  pdf: {
    bg: '#FFCDD2',
    color: '#C62828',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8.5 17.5c-.3 0-.5-.1-.6-.2-.3-.3-.4-.8-.1-1.4.3-.6.8-1.2 1.5-1.7-.4-.9-.7-1.8-.8-2.6H9c.1.6.3 1.3.6 1.9.4-.2.9-.3 1.4-.3.8 0 1.2.4 1.2.9s-.4.9-1.2.9c-.3 0-.7-.1-1-.2-.3.3-.5.6-.6.9-.1.3-.1.5 0 .6.1.2.3.2.6.2z"/>
      </svg>
    ),
  },
  doc: {
    bg: '#BBDEFB',
    color: '#1565C0',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-2 4H7v2h4v-2zm4 4H7v2h8v-2z"/>
      </svg>
    ),
  },
  excel: {
    bg: '#C8E6C9',
    color: '#2E7D32',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-2 6l-2 2 2 2-1 1-2-2-2 2-1-1 2-2-2-2 1-1 2 2 2-2 1 1z"/>
      </svg>
    ),
  },
  powerpoint: {
    bg: '#FFE0B2',
    color: '#E65100',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-5 4h3c1.1 0 2 .9 2 2s-.9 2-2 2H9v2H8v-6h0zm1 3h2c.55 0 1-.45 1-1s-.45-1-1-1H9v2z"/>
      </svg>
    ),
  },
  text: {
    bg: '#E0E0E0',
    color: '#424242',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-6 4h10v2H7v-2zm0 4h10v2H7v-2z"/>
      </svg>
    ),
  },
  archive: {
    bg: '#E1BEE7',
    color: '#6A1B9A',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.24 5h11.52l.83 1H5.42l.82-1zM5 19V8h14v11H5zm8.45-9h-2.9v3H8l4 4 4-4h-2.55z"/>
      </svg>
    ),
  },
  image: {
    bg: '#F8BBD0',
    color: '#C2185B',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
      </svg>
    ),
  },
  video: {
    bg: '#B2DFDB',
    color: '#00695C',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM10 15l-3.89-2.25v-1.5L10 9v6z"/>
      </svg>
    ),
  },
  audio: {
    bg: '#C5CAE9',
    color: '#283593',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 3v9.28a4.39 4.39 0 0 0-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/>
      </svg>
    ),
  },
  file: {
    bg: '#E0E0E0',
    color: '#757575',
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/>
      </svg>
    ),
  },
};

const FileTypeIcon: React.FC<TypeIconProps> = ({ type, size = 40 }) => {
  const config = TYPE_ICON_CONFIG[type] || TYPE_ICON_CONFIG.file;
  return (
    <Box
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        background: config.bg,
        color: config.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {config.svg}
    </Box>
  );
};

interface CurrentFileDisplayProps {
  filename: string;
  launchUrl: string;
}

/**
 * Displays the current file as a clickable link with an appropriate file type icon.
 * Used in EditResourcePage when editing an existing file resource.
 */
const CurrentFileDisplay: React.FC<CurrentFileDisplayProps> = ({ filename, launchUrl }) => {
  const fileType = getFileType(filename);
  const extension = getFileExtension(filename);

  return (
    <a
      href={launchUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${filename} (opens in a new tab)`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        border: '1px solid #E0E0E0',
        borderRadius: 8,
        cursor: 'pointer',
        backgroundColor: '#FAFAFA',
        transition: 'background-color 0.2s',
        textDecoration: 'none',
        color: 'inherit',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#F5F5F5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#FAFAFA';
      }}
    >
      <FileTypeIcon type={fileType} size={40} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="p-md"
          style={{
            color: '#1976D2',
            fontWeight: 500,
            textDecoration: 'underline',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {filename}
        </Typography>
        {extension && (
          <Typography variant="p-sm" style={{ color: '#757575', fontSize: 12 }}>
            {extension.toUpperCase()} file
          </Typography>
        )}
      </Box>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#757575" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
};

export default CurrentFileDisplay;

export interface SelectedFileDisplayProps {
  file: File;
  onRemove: () => void;
}

export const SelectedFileDisplay: React.FC<SelectedFileDisplayProps> = ({ file, onRemove }) => {
  const fileType = getFileType(file.name);
  const sizeLabel =
    file.size >= 1048576
      ? `${(file.size / 1048576).toFixed(1)} MB`
      : `${(file.size / 1024).toFixed(1)} KB`;

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        border: '1px solid #E0E0E0',
        borderRadius: 8,
        backgroundColor: '#FAFAFA',
      }}
    >
      <FileTypeIcon type={fileType} size={40} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="p-md"
          style={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {file.name}
        </Typography>
        <Typography variant="p-sm" style={{ color: '#757575', fontSize: 12 }}>
          {sizeLabel}
        </Typography>
      </Box>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove file"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
          borderRadius: '50%',
          color: '#757575',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.color = '#424242';
          e.currentTarget.style.backgroundColor = '#E0E0E0';
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.color = '#757575';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </button>
    </Box>
  );
};
